import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts'

interface AnalyticsEvent {
  campaignId: string
  subscriberEmail: string
  eventType: 'open' | 'click'
  timestamp: string
}

interface BehavioralSegmentationTabProps {
  analytics: AnalyticsEvent[]
}

function getBehavioralClusters() {
  return [
    { behavior: 'Opens', highlyEngaged: 95, moderatelyEngaged: 65, lowEngagement: 25 },
    { behavior: 'Clicks', highlyEngaged: 85, moderatelyEngaged: 45, lowEngagement: 10 },
    { behavior: 'Time Spent', highlyEngaged: 90, moderatelyEngaged: 55, lowEngagement: 20 },
    { behavior: 'Shares', highlyEngaged: 75, moderatelyEngaged: 35, lowEngagement: 5 },
    { behavior: 'Replies', highlyEngaged: 70, moderatelyEngaged: 30, lowEngagement: 8 }
  ]
}

function getChurnRiskSegments() {
  return [
    { risk: 'Critical Risk', count: 125, percentage: 8 },
    { risk: 'High Risk', count: 245, percentage: 16 },
    { risk: 'Medium Risk', count: 380, percentage: 25 },
    { risk: 'Low Risk', count: 750, percentage: 51 }
  ]
}

function getSubscriberJourneyPaths() {
  return [
    { path: ['Subscribed', 'First Open', 'Clicked', 'Purchased'], count: 245, conversionRate: 18 },
    { path: ['Subscribed', 'First Open', 'Clicked'], count: 512, conversionRate: 12 },
    { path: ['Subscribed', 'First Open'], count: 890, conversionRate: 5 },
    { path: ['Subscribed', 'Never Opened'], count: 156, conversionRate: 0 }
  ]
}

export default function BehavioralSegmentationTab({ analytics }: BehavioralSegmentationTabProps) {
  return (
    <div className="space-y-8">
      {/* Behavioral Clusters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Behavioral Clusters</h2>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={getBehavioralClusters()}>
            <PolarGrid />
            <PolarAngleAxis dataKey="behavior" />
            <PolarRadiusAxis />
            <Radar name="Highly Engaged" dataKey="highlyEngaged" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
            <Radar name="Moderately Engaged" dataKey="moderatelyEngaged" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
            <Radar name="Low Engagement" dataKey="lowEngagement" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} />
            <Legend />
            <Tooltip />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Churn Risk Analysis */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Churn Risk Analysis</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {getChurnRiskSegments().map((segment, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">{segment.risk}</p>
              <p className="text-2xl font-bold text-gray-900">{segment.count}</p>
              <p className="text-xs text-gray-500 mt-1">{segment.percentage}% of total</p>
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={getChurnRiskSegments()}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ risk, percentage }) => `${risk}: ${percentage}%`}
              outerRadius={90}
              fill="#8884d8"
              dataKey="count"
            >
              {getChurnRiskSegments().map((entry, index) => (
                <Cell key={`cell-${index}`} fill={
                  entry.risk === 'Critical Risk' ? '#ef4444' :
                  entry.risk === 'High Risk' ? '#f59e0b' :
                  entry.risk === 'Medium Risk' ? '#3b82f6' :
                  '#10b981'
                } />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Subscriber Journey Paths */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Top Subscriber Journey Paths</h2>
        <div className="space-y-3">
          {getSubscriberJourneyPaths().map((path, idx) => (
            <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm">
                  {path.path.map((step, sIdx) => (
                    <span key={sIdx} className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-white rounded border border-gray-200 font-medium">{step}</span>
                      {sIdx < path.path.length - 1 && <span className="text-gray-400">→</span>}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{path.count} users</p>
                <p className="text-xs text-gray-500">{path.conversionRate}% conversion</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
